import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Upload, FileText, AlertTriangle, CheckCircle2, Clock, Eye, Edit, Trash2, Search } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadDocDialog, DocumentViewDialog, EditDocDialog } from "@/components/EntityDialogs";

export default function Documents() {
  const { documents, getPerson, deleteDocument } = useFgp();
  const [filter, setFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [q, setQ] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const types = ["All", ...Array.from(new Set(documents.map((d) => d.type)))];
  const filtered = documents.filter((d) =>
    (filter === "All" || d.status === filter) &&
    (typeFilter === "All" || d.type === typeFilter) &&
    (d.name.toLowerCase().includes(q.toLowerCase()) || d.type.toLowerCase().includes(q.toLowerCase()))
  );
  const expiring = documents.filter((d) => d.status === "expiring").length;
  const overdue = documents.filter((d) => d.status === "overdue").length;
  const valid = documents.filter((d) => d.status === "valid").length;

  const ownerName = (id) => id === "ws" ? "Household" : getPerson(id)?.name || "—";

  return (
    <div>
      <PageHeader title="Documents" subtitle="Passports, visas, insurance and legal papers with expiry tracking."
        actions={<Button onClick={() => setUploadOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="upload-doc"><Upload className="h-4 w-4" /> Upload</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total documents" value={documents.length} icon={FileText} tone="blue" testid="doc-stat-total" />
        <StatCard label="Valid" value={valid} icon={CheckCircle2} tone="emerald" testid="doc-stat-valid" />
        <StatCard label="Expiring soon" value={expiring} icon={Clock} tone="amber" testid="doc-stat-expiring" />
        <StatCard label="Overdue" value={overdue} icon={AlertTriangle} tone="rose" testid="doc-stat-overdue" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-9 bg-white" data-testid="doc-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["All", "valid", "expiring", "overdue"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`doc-filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filter === s ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {types.map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)} data-testid={`doc-type-filter-${t}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${typeFilter === t ? "bg-neutral-100 text-neutral-700 ring-1 ring-inset ring-neutral-300" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{t}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No documents found</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Upload passports, visas, insurance and legal papers to track expiry.</p>
          <Button onClick={() => setUploadOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-upload-doc"><Upload className="h-4 w-4" /> Upload document</Button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Document</TableHead>
                <TableHead className="hidden sm:table-cell">Type</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-medium text-slate-800"><button onClick={() => setViewDoc(d)} className="flex items-center gap-2 hover:text-neutral-700 text-left"><FileText className="h-4 w-4 text-slate-400 flex-shrink-0" /> {d.name}</button></TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-500">{d.type}</TableCell>
                  <TableCell className="hidden md:table-cell text-slate-500">{ownerName(d.person)}</TableCell>
                  <TableCell className="text-slate-600">{d.expiry}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setViewDoc(d)} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-neutral-600 hover:bg-slate-50" data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4" /></button>
                      <button onClick={() => setEditDoc(d)} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-neutral-600 hover:bg-slate-50" data-testid={`doc-edit-${d.id}`}><Edit className="h-4 w-4" /></button>
                      <button onClick={() => setToDelete(d)} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <UploadDocDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      <DocumentViewDialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)} doc={viewDoc} ownerName={viewDoc ? ownerName(viewDoc.person) : ""} />
      <EditDocDialog open={!!editDoc} onOpenChange={(o) => { if (!o) setEditDoc(null); }} doc={editDoc} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.name}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-doc"
              onClick={() => { deleteDocument(toDelete.id); toast.success("Document deleted"); setToDelete(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
